import * as React from 'react';

import {
    type AsyncDebounceFn,
    type AsyncDebounceOptions,
    type AsyncDebounceScheduler,
    asyncDebounce,
} from '@gitbook/util-async-iterable';

type ScheduledCallback =
    | {
          type: 'idle';
          id: number;
      }
    | {
          type: 'timeout';
          id: ReturnType<typeof setTimeout>;
      };

type BrowserScheduling = typeof globalThis & {
    requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number;
    cancelIdleCallback?: (id: number) => void;
};

const IDLE_CALLBACK_TIMEOUT = 1000;

const BROWSER_SCHEDULING = globalThis as BrowserScheduling;

const IDLE_SCHEDULER: AsyncDebounceScheduler<ScheduledCallback> = {
    schedule: (callback) => {
        if (typeof BROWSER_SCHEDULING.requestIdleCallback === 'function') {
            return {
                type: 'idle',
                id: BROWSER_SCHEDULING.requestIdleCallback(callback, {
                    timeout: IDLE_CALLBACK_TIMEOUT,
                }),
            };
        }

        return {
            type: 'timeout',
            id: setTimeout(callback, 0),
        };
    },
    cancel: (scheduledCallback) => {
        if (scheduledCallback.type === 'idle') {
            BROWSER_SCHEDULING.cancelIdleCallback?.(scheduledCallback.id);
        } else {
            clearTimeout(scheduledCallback.id);
        }
    },
};

type DebounceOptions = Omit<AsyncDebounceOptions, 'scheduler'>;

/**
 * Create a smart debounced function that will execute the passed callback
 */
export function createDebouncedFlush<T>(
    delay: number,
    options: DebounceOptions = {}
): AsyncDebounceFn<T> {
    return asyncDebounce(delay, {
        ...options,
        scheduler: IDLE_SCHEDULER,
    });
}

/**
 * Smart debouncer with low priority.
 */
export function useDebouncedFlush<T = any>(
    delay: number,
    options: DebounceOptions = {}
): AsyncDebounceFn<T> {
    return React.useMemo(
        () =>
            createDebouncedFlush<T>(delay, {
                maxWait: options.maxWait,
                immediate: options.immediate,
            }),
        [delay, options.maxWait, options.immediate]
    );
}

/**
 * Defer a callback with low priority and a delay. it ensures the callback if flushed before unmounting.
 */
export function useDeferredFlush<T = any>(
    delay: number,
    options?: DebounceOptions
): AsyncDebounceFn<T> {
    const result = useDebouncedFlush<T>(delay, options);

    React.useEffect(() => {
        return () => {
            result.flush();
        };
    }, [result]);

    return result;
}
